<template>
  <div class="d-wizard">
    <div class="d-wizard__filters" :class="{'d-wizard__filters--locked': isClosed}">
      <div class="d-input-group">
        <label class="d-label marg-05-bottom">Vælg medarbejder</label>
        <select class="do-select" v-model="userId" :disabled="isClosed">
          <option :value="null" disabled>Vælg person</option>
          <option v-for="user in users" :key="user.id" :value="user.id">
            {{ user.name }}
          </option>
        </select>
      </div>
      <div class="d-input-group">
        <label class="d-label">Første og sidste arbejdsdag (begge dage inklusiv)</label>
        <DatePickerRange
          :startdate="startDate"
          :enddate="endDate"
          :allowSameDate="true"
          @startchanged="setStartDate"
          @endchanged="setEndDate">
        </DatePickerRange>
      </div>
    </div>

    <p class="d-wizard__error" v-if="errorMessage">{{ errorMessage }}</p>
    <p class="text-explainer" v-if="!isPeriodValid">Vælg en medarbejder og en periode for at se opgørelsen.</p>
    <div v-else-if="isLoadingStatement && !statement" class="d-wizard__loading">Henter timeopgørelse...</div>

    <template v-if="isPeriodValid && statement">
      <div class="d-wizard__status" :class="{'d-wizard__status--closed': isClosed}">
        <span v-if="isClosed">
          <b>Opgørelsen er lukket</b> {{ closedLabel }}. Genåbn den for at ændre periode eller opgørelsesmetode.
        </span>
        <span v-else>Opgørelsen er åben og kan ændres. Luk den, når den er færdig.</span>
        <button class="do-bttn"
                :class="{'do-bttn--submit': !isClosed}"
                :disabled="isTogglingClosed || isLoadingStatement"
                @click="setClosed(!isClosed)">
          {{ isClosed ? 'Genåbn opgørelse' : 'Gem og luk opgørelse' }}
        </button>
      </div>
      <div class="d-overview">
        <section class="d-overview__col">
          <h3 class="d-overview__title">Oprindelig <span>{{ schoolYearName }}</span></h3>
          <div v-if="isLoadingOriginal" class="d-wizard__loading">Henter årsnorm...</div>
          <template v-else>
            <table class="d-table d-table__overview">
              <thead>
              <tr>
                <th><h4>Grundoplysninger</h4></th>
                <th class="is-number">Timer</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in originalNormBody" :key="item.title">
                <td>{{ item.title }}</td>
                <td class="is-number">{{ item.value }}</td>
              </tr>
              </tbody>
              <tfoot v-if="originalNormFooter">
              <tr>
                <td>{{ originalNormFooter.title }}</td>
                <td class="is-number">{{ originalNormFooter.value }}</td>
              </tr>
              </tfoot>
            </table>
            <table class="d-table d-table__overview">
              <thead>
              <tr>
                <th><h4>Fordeling</h4></th>
                <th class="is-number"></th>
                <th class="is-number">Tildelt</th>
                <th class="is-number">Skemalagt</th>
                <th class="is-number">Forskel</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Arbejdstid</td>
                <td class="is-number">{{ formatShare(originalWorkHours) }}</td>
                <td class="is-number">{{ format(originalWorkHours) }}</td>
                <td class="is-number">{{ format(originalScheduledTotal) }}</td>
                <td class="is-number">{{ format(originalWorkHours - originalScheduledTotal) }}</td>
              </tr>
              <tr v-for="type in summaryTypes" :key="'original-' + type">
                <td>{{ getMasterTypeTitle(type) }}</td>
                <td class="is-number">{{ formatShare(originalByType(type).assigned) }}</td>
                <td class="is-number">{{ format(originalByType(type).assigned) }}</td>
                <td class="is-number">{{ format(originalByType(type).scheduled) }}</td>
                <td class="is-number">{{ format(originalByType(type).assigned - originalByType(type).scheduled) }}</td>
              </tr>
              <tr>
                <td>
                  Rest
                  <span v-if="restLabel(originalRest)" :class="['d-overview__rest-status', restBadgeClass(originalRest)]">{{ restLabel(originalRest) }}</span>
                </td>
                <td class="is-number">{{ formatShare(originalRest) }}</td>
                <td class="is-number"><span :class="restBadgeClass(originalRest)">{{ formatSigned(originalRest) }}</span></td>
                <td></td>
                <td></td>
              </tr>
              </tbody>
            </table>
          </template>
        </section>

        <section class="d-overview__col d-overview__col--active">
          <h3 class="d-overview__title">Opgjort <span>{{ periodFormatted }}</span></h3>
          <table class="d-table d-table__overview">
            <thead>
            <tr>
              <th><h4>Grundoplysninger</h4></th>
              <th class="is-number">Timer</th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td>Periode</td>
              <td class="is-number">{{ periodFormatted }}</td>
            </tr>
            <tr>
              <td>Ansættelsesgrad</td>
              <td class="is-number">{{ employmentPercentLabel }}</td>
            </tr>
            <tr>
              <td>Timeansættelse pr. uge</td>
              <td class="is-number">{{ format(hoursPerWeek) }}</td>
            </tr>
            <tr>
              <td>Bruttotid <span class="d-overview__days">({{ formatDays(statement.brutto_dage) }} dage)</span></td>
              <td class="is-number">{{ format(statement.brutto_tid_i_timer) }}</td>
            </tr>
            <tr>
              <td>
                Søgnehelligdage <span class="d-overview__days">({{ formatDays(statement.helligdage_dage) }} dage)</span>
                <span v-if="holidays.length" class="icon-question3"
                      :content="holidaysTooltip" v-tippy="{arrow: true, zIndex: 999999992}"></span>
              </td>
              <td class="is-number">{{ format(statement.soegne_helligdage_i_timer) }}</td>
            </tr>
            <tr>
              <td>
                <div class="d-overview__vacation">
                  Ferie
                  <input type="text" inputmode="decimal" class="d-overview__input"
                         :value="numberInput('vacation_days_taken')" :disabled="isClosed"
                         @focus="$event.target.select()"
                         @mouseup.prevent
                         @input="setNumber('vacation_days_taken', $event.target.value)"
                         @blur="flushNumberSave('vacation_days_taken')">
                  <span class="d-overview__days">dage afholdt i perioden</span>
                </div>
              </td>
              <td class="is-number">{{ format(statement.feriedage_i_timer) }}</td>
            </tr>
            <tr>
              <td>Elevdage i perioden</td>
              <td class="is-number">{{ schoolDaysInPeriod }}</td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
              <td>Arbejdstid <span class="d-overview__days">({{ formatDays(statement.arbejdsdage) }} dage)</span></td>
              <td class="is-number">{{ format(periodWorkHours) }}</td>
            </tr>
            </tfoot>
          </table>
          <table class="d-table d-table__overview">
            <thead>
            <tr>
              <th><h4>Fordeling</h4></th>
              <th class="is-number">Timer i perioden</th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td>Arbejdstid</td>
              <td class="is-number">
                <span class="icon-question3 d-overview__help" :content="workHoursExplanation" v-tippy="explanationTippy"></span>
                {{ format(periodWorkHours) }}
              </td>
            </tr>
            <tr v-for="type in summaryTypes" :key="'period-' + type">
              <td>{{ getMasterTypeTitle(type) }}</td>
              <td class="is-number">
                <span class="icon-question3 d-overview__help" :content="typeExplanation(type)" v-tippy="explanationTippy"></span>
                {{ format(groupHoursByType(type)) }}
              </td>
            </tr>
            <tr>
              <td>
                Rest
                <span v-if="restLabel(rest)" :class="['d-overview__rest-status', restBadgeClass(rest)]">{{ restLabel(rest) }}</span>
              </td>
              <td class="is-number">
                <span class="icon-question3 d-overview__help" :content="restExplanation" v-tippy="explanationTippy"></span>
                <span :class="restBadgeClass(rest)">{{ formatSigned(rest) }}</span>
              </td>
            </tr>
            </tbody>
          </table>
        </section>
      </div>

      <div class="d-wizard__intro">
        <h3 class="d-overview__title">Timeopgørelse for perioden</h3>
        <div class="do-warning d-wizard__legend">
          <b>Opgørelsesmetode:</b>
          <ol class="d-wizard__legend-list">
            <li v-for="method in methods" :key="method.id"><b>{{ method.title }}:</b> {{ method.description }}</li>
          </ol>
        </div>
      </div>

      <p v-if="!groups.length">Der er ingen opgaver for personen i perioden.</p>
        <table class="do-table" :class="{'do-table--locked': isClosed}" v-for="group in groups" :key="group.type">
          <thead>
          <tr>
            <th rowspan="2">{{ group.title }}</th>
            <th rowspan="2" class="d-dates">Dato</th>
            <th rowspan="2">Tildelt</th>
            <th rowspan="2">Skemalagt</th>
            <th class="d-method-group" :colspan="methods.length"
                content="Vælg én metode for hver linje. Timerne i perioden regnes ud fra den valgte metode."
                v-tippy="{arrow: true, zIndex: 999999992}">
              Vælg opgørelsesmetode <span class="icon-question3"></span>
            </th>
            <th rowspan="2"
                content="Timerne i perioden efter den valgte metode. Ved Egen vurdering tastes tallet her."
                v-tippy="{arrow: true, zIndex: 999999992}">
              Timer i perioden <span class="icon-question3"></span>
            </th>
          </tr>
          <tr>
            <th v-for="method in methods" :key="method.id" class="d-method"
                :content="method.description" v-tippy="{arrow: true, zIndex: 999999992}">
              {{ method.title }} <span class="icon-question3"></span>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in group.rows" :key="row.key">
            <td>{{ row.task.name }}<span v-if="row.task.substitute"> (vikar)</span></td>
            <td class="d-dates">{{ formatDateRange(row.task.from_date, row.task.to_date) }}</td>
            <td>{{ format(row.assigned) }}</td>
            <td>{{ format(row.scheduled) }}</td>
            <td v-for="method in methods" :key="method.id"
                class="d-method" :class="{'d-method--selected': rowState(row).method === method.id}"
                @click="setMethod(row, method.id)">
              <label class="d-method__label">
                <input type="radio" :name="'method-' + row.key" :value="method.id"
                       :checked="rowState(row).method === method.id"
                       :disabled="isClosed"
                       @change="setMethod(row, method.id)">
              </label>
            </td>
            <td class="d-period-hours">
              <input v-if="isManualMethod(rowState(row).method)" :ref="'period-' + row.key"
                     type="text" inputmode="decimal" class="d-method__input"
                     :value="rowState(row).manual[rowState(row).method]"
                     :disabled="isClosed"
                     @input="setManual(row, rowState(row).method, $event.target.value)">
              <b v-else>{{ format(rowHours(row)) }}</b>
            </td>
          </tr>
          <tr class="d-diff">
            <td>Total</td>
            <td></td>
            <td>{{ format(group.assigned) }}</td>
            <td>{{ format(group.scheduled) }}</td>
            <td :colspan="methods.length"></td>
            <td>{{ format(groupHours(group)) }}</td>
          </tr>
          </tbody>
        </table>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import docendo from '../../docendo'
import DatePickerRange from '../datepicker/DatePickerRange.vue'
import {parseNumericPreparationFactor, effectivePreparationFactor} from '../../utils/preparationFactor'

const METHODS = [
  {id: 'scheduled', title: 'Skemalagt', api: 'skemadage', description: 'Timerne, der er skemalagt i perioden.'},
  {id: 'school_days', title: 'Elevdage', api: 'skoledage', description: 'Tildelte timer fordelt efter antal elevdage i perioden.'},
  {id: 'actual_days', title: 'Faktiske dage', api: 'faktiskedage', description: 'Tildelte timer fordelt på opgavens dage i perioden.'},
  {id: 'assessment', title: 'Egen vurdering', api: 'vurdering', description: 'Du angiver selv timerne for perioden.', isManual: true}
]
const NUMBER_FIELDS = {
  vacation_days_taken: {
    invalid: 'Feriedage skal være et tal, f.eks. 2 eller 2,5.',
    failed: 'Feriedagene kunne ikke gemmes. Prøv igen.'
  }
}
const HOURS_PER_FULL_TIME_WEEK = 37
const MASTER_TYPE_ORDER = ['teaching', 'preparation', 'other', 'pool', 'club', 'with_kids', 'without_kids', 'flextime', 'absence', 'unknown']
const API_TO_METHOD = METHODS.reduce((map, method) => {
  map[method.api] = method.id
  return map
}, {})
function toNumber (value) {
  const num = parseFloat(value)
  return isNaN(num) ? 0 : num
}

function toNullableNumber (value) {
  if (value === '' || value === null || value === undefined) return null
  const num = parseFloat(String(value).replace(',', '.'))
  return isNaN(num) ? null : num
}

function escapeHtml (value) {
  return String(value).replace(/[&<>"']/g, char => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[char]))
}

function countWeekdays (from, to, excludedDays) {
  let count = 0
  let day = moment(from)
  const end = moment(to)
  while (day.isSameOrBefore(end, 'day')) {
    const isWeekend = day.isoWeekday() === 6 || day.isoWeekday() === 7
    if (!isWeekend && !(excludedDays && excludedDays[day.format('YYYY-MM-DD')])) {
      count++
    }
    day = day.add(1, 'days')
  }
  return count
}

export default {
  components: {DatePickerRange},
  props: {
    schoolId: {},
    users: {
      type: Array,
      default: () => []
    },
    savedStatement: {
      type: Object,
      default: null
    }
  },
  data () {
    const schoolYear = this.$store.state.localActivatedSchoolYear
    const saved = this.savedStatement
    return {
      methods: METHODS,
      userId: saved ? saved.user_id : null,
      startDate: saved ? saved.from_date : schoolYear ? moment(schoolYear.employee_time_start).format('YYYY-MM-DD') : null,
      endDate: saved ? saved.to_date : moment().endOf('month').format('YYYY-MM-DD'),
      statement: null,
      isLoadingStatement: false,
      isTogglingClosed: false,
      numberDrafts: {vacation_days_taken: null},
      errorMessage: null,
      rows: {},
      userYear: null,
      yearTasks: [],
      isLoadingOriginal: false
    }
  },
  computed: {
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    schoolYearName () {
      return this.activeSchoolYear ? this.activeSchoolYear.name : ''
    },
    period () {
      return {userId: this.userId, startDate: this.startDate, endDate: this.endDate}
    },
    isPeriodValid () {
      return Boolean(this.userId) && moment(this.startDate).isValid() && moment(this.endDate).isValid() &&
        !moment(this.endDate).isBefore(this.startDate)
    },
    periodKey () {
      return this.isPeriodValid ? [this.userId, this.startDate, this.endDate].join('|') : null
    },
    originalNorm () {
      const norm = this.userYear && Array.isArray(this.userYear.norm) ? this.userYear.norm : []
      return norm.filter(item => item && item.title && item.title.indexOf('hidden_') !== 0)
    },
    originalNormBody () {
      return this.originalNorm.slice(0, -1)
    },
    originalNormFooter () {
      return this.originalNorm[this.originalNorm.length - 1]
    },
    originalWorkHours () {
      return toNumber(this.userYear && this.userYear.assigned_total_hours)
    },
    originalTypes () {
      const types = {}
      this.yearTasks.forEach(task => {
        if (!types[task.master_type]) {
          types[task.master_type] = {assigned: 0, scheduled: 0}
        }
        types[task.master_type].assigned += toNumber(task.hours)
        types[task.master_type].scheduled += toNumber(task.actual)
      })
      return types
    },
    originalScheduledTotal () {
      return this.summaryTypes.reduce((sum, type) => sum + this.originalByType(type).scheduled, 0)
    },
    originalRest () {
      return this.summaryTypes.reduce((sum, type) => sum - this.originalByType(type).assigned, this.originalWorkHours)
    },
    summaryTypes () {
      const types = new Set(this.groups.map(group => group.type))
      Object.keys(this.originalTypes).forEach(type => {
        const values = this.originalTypes[type]
        if (values.assigned !== 0 || values.scheduled !== 0) types.add(type)
      })
      if (this.originalPreparation !== 0 || this.periodPreparation !== 0) types.add('preparation')
      types.delete('absence')
      return this.sortMasterTypes([...types])
    },
    hoursPerWeek () {
      return HOURS_PER_FULL_TIME_WEEK * toNumber(this.statement && this.statement.employment_percent)
    },
    selectedUserName () {
      const user = this.users.find(user => user.id === this.period.userId)
      return user ? user.name : ''
    },
    statementTitle () {
      if (!this.selectedUserName || !moment(this.period.startDate).isValid() || !moment(this.period.endDate).isValid()) {
        return 'Opgørelse af ansættelse'
      }
      return 'Opgørelse af ansættelse for ' + this.selectedUserName + ', ' + this.periodFormatted
    },
    isClosed () {
      return Boolean(this.statement && this.statement.closed_at)
    },
    closedLabel () {
      if (!this.isClosed) return ''
      const closedBy = this.users.find(user => Number(user.id) === Number(this.statement.closed_by_id)) ||
        this.$store.getters.allUsersInHash[this.statement.closed_by_id]
      const label = 'd. ' + moment(this.statement.closed_at).format('LLL')
      return closedBy ? label + ' af ' + closedBy.name : label
    },
    periodFormatted () {
      return moment(this.period.startDate).format('LL') + ' - ' + moment(this.period.endDate).format('LL')
    },
    schoolDaysInPeriod () {
      return Math.round(toNumber(this.statement && this.statement.skoledage))
    },
    holidays () {
      return (this.statement && this.statement.helligdage) || []
    },
    holidaysTooltip () {
      return this.holidays.map(([date, name]) => moment(date).format('D. MMM YYYY') + ' ' + name).join(', ')
    },
    // The school's preparation factor when the weighted model applies (DK, pools deactivated), otherwise null
    prepFactor () {
      const settings = this.$store.state.schoolSettings || {}
      const isDanish = this.$store.getters.locale === 'da' && String(this.$store.getters.schoolCountry).toLowerCase() === 'dk'
      if (!isDanish || !this.isPoolsDeactivated) return null
      return parseNumericPreparationFactor(settings.prepFactor)
    },
    isPoolsDeactivated () {
      return Boolean((this.$store.state.schoolSettings || {}).deactivatePools)
    },
    isUserInGroupWithoutPrepFactor () {
      const tagsWithout = this.$store.getters.userTagsWithoutPrepFactor || []
      const user = this.$store.getters.allUsersInHash && this.$store.getters.allUsersInHash[this.userId]
      const userTags = user && Array.isArray(user.user_tags) ? user.user_tags : []
      return userTags.some(tag => tagsWithout.includes(tag))
    },
    // Preparation that is not a task: teaching × factor, or the assigned preparation hours of the norm when the
    // pools are active. Preparation tasks are left out, they are already counted as tasks.
    originalPreparation () {
      if (this.prepFactor === null) {
        if (this.isPoolsDeactivated) return 0
        return toNumber(this.userYear && this.userYear.assigned_preparation_hours)
      }
      if (this.isUserInGroupWithoutPrepFactor) return 0
      return this.yearTasks.reduce((sum, task) => {
        if (task.substitute || task.master_type !== 'teaching' || task.subject_type === 'preparation') return sum
        return sum + toNumber(task.hours) * effectivePreparationFactor(task.preparation, this.prepFactor)
      }, 0)
    },
    periodPreparation () {
      if (this.isClosed) return toNumber(this.statement.preparation_hours)
      if (this.prepFactor === null) {
        return this.originalWorkHours > 0 ? this.originalPreparation * this.periodWorkHours / this.originalWorkHours : 0
      }
      if (this.isUserInGroupWithoutPrepFactor) return 0
      const teaching = this.groups.find(group => group.type === 'teaching')
      if (!teaching) return 0
      return teaching.rows.reduce((sum, row) => {
        if (row.task.substitute || row.task.subject_type === 'preparation') return sum
        return sum + this.rowHours(row) * effectivePreparationFactor(row.task.preparation, this.prepFactor)
      }, 0)
    },
    // The tooltip keeps its first content, so the current content attribute is read on every show
    explanationTippy () {
      return {
        arrow: true,
        allowHTML: true,
        placement: 'left',
        maxWidth: 420,
        zIndex: 999999992,
        onShow: instance => instance.setContent(instance.reference.getAttribute('content'))
      }
    },
    workHoursExplanation () {
      const statement = this.statement || {}
      const percent = Math.round(toNumber(statement.employment_percent) * 100) + ' %'
      return '<b>Arbejdstid i perioden</b><br>' +
        'Hverdage i perioden minus søgnehelligdage og feriedage, ganget med 7,4 timer og ansættelsesgraden.<br><br>' +
        '<b>Regneeksempel</b><br>' +
        this.formatDays(statement.brutto_dage) + ' − ' + this.formatDays(statement.helligdage_dage) + ' − ' +
        this.formatDays(statement.feriedage) + ' = ' + this.formatDays(statement.arbejdsdage) + ' arbejdsdage<br>' +
        this.formatDays(statement.arbejdsdage) + ' × 7.4 × ' + percent + ' = ' + this.format(this.periodWorkHours)
    },
    restExplanation () {
      const parts = this.summaryTypes.map(type => this.format(this.groupHoursByType(type)))
      // The backend still subtracts absence from the rest
      const absence = this.groupHoursByType('absence')
      if (absence !== 0) parts.push(this.format(absence) + ' (fravær)')
      return '<b>Rest</b><br>' +
        'Arbejdstiden minus alle opgaver og forberedelse i perioden.<br><br>' +
        '<b>Regneeksempel</b><br>' +
        [this.format(this.periodWorkHours), ...parts].join(' − ') + ' = ' + this.format(this.rest)
    },
    // The fallback needs the norm before the value is known
    preparationToSave () {
      if (!this.statement || this.isClosed || this.isLoadingStatement) return null
      if (this.prepFactor === null && !this.isPoolsDeactivated && (this.isLoadingOriginal || !this.userYear)) return null
      return Math.round(this.periodPreparation * 100) / 100
    },
    preparationSyncKey () {
      if (this.preparationToSave === null) return null
      return this.preparationToSave + '|' + this.statement.preparation_hours
    },
    schoolDaysInYear () {
      return Math.round(toNumber(this.statement && this.statement.school_year_work_days))
    },
    schoolDaysRatio () {
      return this.schoolDaysInYear > 0 ? this.schoolDaysInPeriod / this.schoolDaysInYear : 0
    },
    // calculated_values follows the order of statement.tasks, so rows are matched by index
    calculatedValues () {
      return (this.statement && this.statement.calculated_values) || []
    },
    savedMethods () {
      return ((this.statement && this.statement.calculation_method) || []).reduce((map, item) => {
        map[item.id] = API_TO_METHOD[item.method]
        return map
      }, {})
    },
    employmentPercentLabel () {
      return Math.round(toNumber(this.statement && this.statement.employment_percent) * 100) + ' %'
    },
    periodWorkHours () {
      return toNumber(this.statement && this.statement.arbejds_tid_i_timer)
    },
    localPoolTitle () {
      return this.$store.getters.localPoolTitle || this.$t('vue2.task_overview.pool')
    },
    groups () {
      if (!this.statement) {
        return []
      }
      const groups = {}
      this.statement.tasks
        .forEach((task, index) => {
          if (toNumber(task.hours) === 0 && toNumber(task.actual) === 0) return
          const type = task.master_type
          if (!groups[type]) {
            const order = MASTER_TYPE_ORDER.indexOf(type)
            groups[type] = {
              type,
              title: this.getMasterTypeTitle(type),
              order: order === -1 ? MASTER_TYPE_ORDER.length : order,
              rows: [],
              assigned: 0,
              scheduled: 0
            }
          }
          const row = {
            key: task.id + (task.substitute ? '-substitute' : ''),
            index,
            task,
            assigned: toNumber(task.hours),
            scheduled: toNumber(task.actual)
          }
          groups[type].rows.push(row)
          groups[type].assigned += row.assigned
          groups[type].scheduled += row.scheduled
        })
      return Object.values(groups)
        .map(group => ({...group, rows: group.rows.sort((a, b) => a.task.name.localeCompare(b.task.name))}))
        .sort((a, b) => a.order - b.order)
    },
    rest () {
      return toNumber(this.statement && this.statement.rest_i_timer)
    }
  },
  watch: {
    statementTitle: {
      handler (title) {
        this.$emit('title', title)
      },
      immediate: true
    },
    periodKey (key) {
      clearTimeout(this._loadTimer)
      if (!key) return
      this._loadTimer = setTimeout(() => this.loadStatement(), 400)
    },
    userId () {
      this.loadOriginal()
    },
    // Also watches the saved value, as an older response can bring back a previous value
    preparationSyncKey (key) {
      if (key !== null) this.schedulePreparationSync()
    }
  },
  methods: {
    setStartDate (date) {
      this.startDate = moment(date).format('YYYY-MM-DD')
    },
    setEndDate (date) {
      this.endDate = moment(date).format('YYYY-MM-DD')
    },
    loadOriginal () {
      const userId = this.userId
      const schoolYear = this.activeSchoolYear
      this.userYear = null
      this.yearTasks = []
      if (!userId || !schoolYear) return
      this.isLoadingOriginal = true
      let pending = 2
      const done = () => {
        pending--
        if (pending === 0) this.isLoadingOriginal = false
      }
      const toDateExclusive = moment(schoolYear.employee_time_end).add(1, 'days').format('YYYY-MM-DD')
      docendo.userTasks(userId, schoolYear.employee_time_start, toDateExclusive, null, data => {
        if (this.userId === userId && Array.isArray(data)) {
          this.yearTasks = data
        }
        done()
      }, done)
      docendo.schoolUserYearRead(this.schoolId, userId, schoolYear.name, data => {
        if (this.userId === userId && data && data.status !== 'error') {
          this.userYear = data
        }
        done()
      }, done)
    },
    originalByType (type) {
      const values = this.originalTypes[type] || {assigned: 0, scheduled: 0}
      if (type !== 'preparation') return values
      return {...values, assigned: values.assigned + this.originalPreparation}
    },
    groupHoursByType (type) {
      const group = this.groups.find(item => item.type === type)
      const hours = group ? this.groupHours(group) : 0
      return type === 'preparation' ? hours + this.periodPreparation : hours
    },
    typeExplanation (type) {
      const title = '<b>' + escapeHtml(this.getMasterTypeTitle(type)) + ' i perioden</b><br>'
      const group = this.groups.find(item => item.type === type)
      if (type === 'preparation') {
        return title + this.preparationExplanation(group)
      }
      if (!group) {
        return title + 'Der er ingen opgaver af denne type i perioden.'
      }
      return title +
        'Summen af opgavernes timer i perioden. Hver opgave er opgjort med den metode, der er valgt for den i tabellen nedenfor.<br><br>' +
        this.tasksExplanation(group)
    },
    tasksExplanation (group) {
      const byMethod = this.methods.map(method => {
        const rows = group.rows.filter(row => this.rowState(row).method === method.id)
        return {method, rows, hours: rows.reduce((sum, row) => sum + this.rowHours(row), 0)}
      }).filter(item => item.rows.length)
      const lines = byMethod.map(item => item.method.title + ' (' + item.rows.length + ' ' +
        (item.rows.length === 1 ? 'opgave' : 'opgaver') + '): ' + this.format(item.hours))
      const examples = byMethod
        .map(item => item.rows.find(row => this.rowHours(row) !== 0))
        .filter(Boolean)
        .map(row => this.rowExplanation(row))
      const total = byMethod.length > 1
        ? '<br>I alt: ' + byMethod.map(item => this.format(item.hours)).join(' + ') + ' = ' + this.format(this.groupHours(group))
        : ''
      return lines.join('<br>') + total +
        (examples.length ? '<br><br><b>Regneeksempel</b><br>' + examples.join('<br>') : '')
    },
    rowExplanation (row) {
      const name = escapeHtml(row.task.name)
      const hours = this.rowHours(row)
      switch (this.rowState(row).method) {
        case 'scheduled':
          return name + ': skemalagt i perioden = ' + this.format(hours)
        case 'school_days':
          return name + ': ' + this.format(row.assigned) + ' tildelt × ' + this.schoolDaysInPeriod + ' / ' +
            this.schoolDaysInYear + ' elevdage = ' + this.format(hours)
        case 'actual_days': {
          const share = row.assigned ? Math.round(hours / row.assigned * 100) : 0
          return name + ': ' + this.format(row.assigned) + ' tildelt × ' + share +
            ' % af opgavens dage ligger i perioden = ' + this.format(hours)
        }
        default:
          return name + ': egen vurdering = ' + this.format(hours)
      }
    },
    preparationExplanation (group) {
      const taskHours = group ? this.groupHours(group) : 0
      const taskLine = group
        ? '<br>+ forberedelsesopgaver: ' + this.format(taskHours) + ' = ' + this.format(this.periodPreparation + taskHours)
        : ''
      if (this.isClosed) {
        return 'Gemt da opgørelsen blev lukket: ' + this.format(this.periodPreparation) + taskLine
      }
      if (this.prepFactor === null && this.isPoolsDeactivated) {
        return 'Skolen har slået puljerne fra og har ingen forberedelsesfaktor, så forberedelsen kommer kun fra forberedelsesopgaverne.' +
          (group ? '<br><br>Forberedelsesopgaver: ' + this.format(taskHours) : '')
      }
      if (this.prepFactor === null) {
        return 'Årets tildelte forberedelse fra årsnormen, fordelt efter periodens andel af årets arbejdstid.<br><br>' +
          '<b>Regneeksempel</b><br>' +
          this.format(this.originalPreparation) + ' × ' + this.format(this.periodWorkHours) + ' / ' +
          this.format(this.originalWorkHours) + ' = ' + this.format(this.periodPreparation) + taskLine
      }
      if (this.isUserInGroupWithoutPrepFactor) {
        return 'Personen er i en gruppe uden forberedelsesfaktor, så undervisningen giver ingen forberedelse.' + taskLine
      }
      const teaching = this.groups.find(item => item.type === 'teaching')
      const byFactor = {}
      ;(teaching ? teaching.rows : []).forEach(row => {
        if (row.task.substitute || row.task.subject_type === 'preparation') return
        const factor = effectivePreparationFactor(row.task.preparation, this.prepFactor)
        byFactor[factor] = (byFactor[factor] || 0) + this.rowHours(row)
      })
      const parts = Object.keys(byFactor).map(factor => this.format(byFactor[factor]) + ' × ' + this.format(factor))
      return 'Undervisningens timer i perioden ganget med forberedelsesfaktoren. Fag med egen faktor bruger den, ellers bruges skolens faktor (' +
        this.format(this.prepFactor) + ').<br><br>' +
        '<b>Regneeksempel</b><br>' +
        (parts.length ? parts.join(' + ') : '0.00') + ' = ' + this.format(this.periodPreparation) + taskLine
    },
    // Concurrent updates of the same statement can lose a response, so they are sent one at a time.
    // Every update carries the calculation methods and the current preparation.
    updateStatement (attributes, onSaved, onFailed) {
      const statementId = this.statement.id
      const preparation = this.preparationToSave
      this._updatesInFlight = (this._updatesInFlight || 0) + 1
      const done = () => {
        this._updatesInFlight--
        this.schedulePreparationSync()
      }
      docendo.updateWorkedHoursStatement(this.schoolId, statementId, {
        ...attributes,
        ...(preparation === null ? {} : {preparation_hours: preparation}),
        calculation_method: this.calculationMethodPayload()
      }, data => {
        if (!data || data.status === 'error') {
          onFailed()
        } else if (this.statement && this.statement.id === statementId) {
          onSaved(data)
          this.statement = {...this.statement, ...data}
        }
        done()
      }, () => {
        onFailed()
        done()
      })
    },
    schedulePreparationSync () {
      clearTimeout(this._preparationSaveTimer)
      this._preparationSaveTimer = setTimeout(() => this.syncPreparation(), 400)
    },
    syncPreparation () {
      const value = this.preparationToSave
      if (value === null || value === toNumber(this.statement.preparation_hours)) return
      if (this._updatesInFlight || this._calculationSaveTimer) return
      this.updateStatement({}, () => {}, () => {})
    },
    // Rounded like the shown number, so 0.004 is not called under or over
    restBadgeClass (rest) {
      const rounded = Math.round(rest * 100) / 100
      if (rounded > 0) return 'd-bagde d-badge--green'
      if (rounded < 0) return 'd-bagde d-badge--warning'
      return ''
    },
    restLabel (rest) {
      const rounded = Math.round(rest * 100) / 100
      if (rounded > 0) return 'Timer til overs'
      if (rounded < 0) return 'Over arbejdstiden – for mange timer'
      return ''
    },
    formatSigned (value) {
      const rounded = Math.round(toNumber(value) * 100) / 100
      return Math.abs(rounded).toFixed(2)
    },
    formatShare (hours) {
      if (!this.originalWorkHours) return ''
      return Math.round(toNumber(hours) / this.originalWorkHours * 100) + ' %'
    },
    getMasterTypeTitle (type) {
      switch (type) {
        case 'teaching':
          return this.$t('vue2.task_overview.teaching')
        case 'other':
          return this.$t('vue2.task_overview.tasks')
        case 'preparation':
          return this.$t('vue2.task_overview.preparation')
        case 'club':
          return this.$t('vue2.task_overview.club')
        case 'pool':
          return this.localPoolTitle
        case 'absence':
          return this.$t('vue2.task_overview.absence')
        case 'unknown':
          return this.$t('vue2.task_overview.unknown')
        case 'with_kids':
          return 'Tid med børn'
        case 'without_kids':
          return 'Tid uden børn'
        case 'flextime':
          return 'Flekstid'
        default:
          return type
      }
    },
    loadStatement () {
      if (!this.isPeriodValid) {
        return
      }
      clearTimeout(this._calculationSaveTimer)
      this._calculationSaveTimer = null
      this.clearNumberDrafts()
      const {userId, startDate, endDate} = this.period
      const statement = this.statement
      const sameUser = statement && Number(statement.user_id) === Number(userId)
      if (sameUser && (this.isClosed || (statement.from_date === startDate && statement.to_date === endDate))) {
        return
      }
      if (!sameUser) {
        this.statement = null
        this.rows = {}
      }
      this.errorMessage = null
      this.isLoadingStatement = true
      const requestKey = this.periodKey
      const onFailure = () => {
        if (requestKey !== this.periodKey) return
        this.isLoadingStatement = false
        this.errorMessage = 'Timeopgørelsen kunne ikke hentes. Prøv igen.'
      }
      const onSuccess = data => {
        if (!data || data.status === 'error') return onFailure()
        if (requestKey !== this.periodKey) return
        this.applyStatement(data)
        this.isLoadingStatement = false
      }
      if (sameUser) {
        docendo.updateWorkedHoursStatement(this.schoolId, statement.id, {
          from_date: startDate,
          to_date: endDate,
          calculation_method: this.calculationMethodPayload()
        }, onSuccess, onFailure)
      } else {
        if (statement && !statement.closed_at) {
          docendo.deleteWorkedHoursStatement(this.schoolId, statement.id, () => {}, () => {})
        }
        docendo.createWorkedHoursStatement(this.schoolId, userId, startDate, endDate, onSuccess, onFailure)
      }
    },
    applyStatement (data) {
      this.clearNumberDrafts()
      this.statement = data
      this.rows = this.hydrateRows(data.calculation_method)
    },
    // Closing sends the pending calculation methods along, so they are saved before the statement locks
    setClosed (closed) {
      if (!this.statement || this.isTogglingClosed) return
      clearTimeout(this._calculationSaveTimer)
      this._calculationSaveTimer = null
      clearTimeout(this._loadTimer)
      const statementId = this.statement.id
      const attributes = closed ? {closed: true, calculation_method: this.calculationMethodPayload()} : {closed: false}
      if (closed && this.preparationToSave !== null) attributes.preparation_hours = this.preparationToSave
      this.errorMessage = null
      this.isTogglingClosed = true
      docendo.updateWorkedHoursStatement(this.schoolId, statementId, attributes, data => {
        this.isTogglingClosed = false
        if (!this.statement || this.statement.id !== statementId) return
        this.statement = data
        this.startDate = data.from_date
        this.endDate = data.to_date
      }, () => {
        this.isTogglingClosed = false
        this.errorMessage = closed ? 'Opgørelsen kunne ikke lukkes. Prøv igen.' : 'Opgørelsen kunne ikke genåbnes. Prøv igen.'
      })
    },
    hydrateRows (calculationMethod) {
      const buckets = {}
      ;(calculationMethod || []).forEach(item => {
        const method = API_TO_METHOD[item.method]
        const id = Number(item.id)
        if (!method || !Number.isInteger(id)) return
        if (!buckets[id]) buckets[id] = []
        buckets[id].push({method, value: item.value})
      })
      const rows = {}
      this.groups.forEach(group => {
        group.rows.forEach(row => {
          const id = Number(row.task.id)
          const bucket = buckets[id]
          if (!bucket || !bucket.length) return
          const siblings = this.groups.reduce((count, item) => {
            return count + item.rows.filter(other => Number(other.task.id) === id).length
          }, 0)
          if (bucket.length < siblings && row.task.substitute) return
          const saved = bucket.shift()
          if (!saved) return
          rows[row.key] = {
            method: saved.method,
            manual: {
              assessment: saved.method === 'assessment' && saved.value != null ? saved.value : 0
            }
          }
        })
      })
      return rows
    },
    // Starts from the saved list so tasks that are not shown keep the method the backend gave them
    calculationMethodPayload () {
      const entries = ((this.statement && this.statement.calculation_method) || []).map(item => ({...item}))
      const indexById = entries.reduce((map, item, index) => {
        map[item.id] = index
        return map
      }, {})
      this.groups.forEach(group => {
        group.rows.forEach(row => {
          const id = Number(row.task.id)
          if (!Number.isInteger(id)) return
          const state = this.rowState(row)
          const method = this.methods.find(item => item.id === state.method)
          if (!method) return
          const entry = {
            id,
            method: method.api,
            value: method.isManual ? toNullableNumber(state.manual[state.method]) : null
          }
          if (id in indexById) {
            entries[indexById[id]] = entry
          } else {
            indexById[id] = entries.push(entry) - 1
          }
        })
      })
      return entries
    },
    scheduleCalculationSave () {
      clearTimeout(this._calculationSaveTimer)
      this._calculationSaveTimer = setTimeout(() => {
        this._calculationSaveTimer = null
        this.saveCalculationMethod()
      }, 400)
    },
    flushCalculationSave () {
      if (!this._calculationSaveTimer) return
      clearTimeout(this._calculationSaveTimer)
      this._calculationSaveTimer = null
      this.saveCalculationMethod()
    },
    saveCalculationMethod () {
      if (!this.statement || this.isClosed) return
      if (this._updatesInFlight) {
        this.scheduleCalculationSave()
        return
      }
      this.updateStatement({}, () => {}, () => {
        this.errorMessage = 'Opgørelsesmetoden kunne ikke gemmes. Prøv igen.'
      })
    },
    sortMasterTypes (types) {
      return types.sort((a, b) => {
        const orderA = MASTER_TYPE_ORDER.indexOf(a)
        const orderB = MASTER_TYPE_ORDER.indexOf(b)
        return (orderA === -1 ? MASTER_TYPE_ORDER.length : orderA) - (orderB === -1 ? MASTER_TYPE_ORDER.length : orderB)
      })
    },
    numberInput (field) {
      if (this.numberDrafts[field] !== null) return this.numberDrafts[field]
      const value = toNumber(this.statement && this.statement[field])
      return String(Math.round(value * 100) / 100).replace('.', ',')
    },
    clearNumberDrafts () {
      Object.keys(this.numberDrafts).forEach(field => {
        clearTimeout(this._numberSaveTimers[field])
        this.numberDrafts[field] = null
      })
      this._numberSaveTimers = {}
    },
    setNumber (field, value) {
      if (this.isClosed) return
      this.numberDrafts[field] = value
      clearTimeout(this._numberSaveTimers[field])
      this._numberSaveTimers[field] = setTimeout(() => this.flushNumberSave(field), 600)
    },
    flushNumberSave (field) {
      clearTimeout(this._numberSaveTimers[field])
      this._numberSaveTimers[field] = null
      const draft = this.numberDrafts[field]
      if (draft === null || !this.statement || this.isClosed) return
      const value = String(draft).trim() === '' ? 0 : toNullableNumber(draft)
      if (value === null || value < 0) {
        this.errorMessage = NUMBER_FIELDS[field].invalid
        return
      }
      if (value === toNumber(this.statement[field])) {
        this.numberDrafts[field] = null
        return
      }
      if (this._updatesInFlight) {
        this._numberSaveTimers[field] = setTimeout(() => this.flushNumberSave(field), 400)
        return
      }
      this.errorMessage = null
      this.updateStatement({[field]: value}, () => {
        this.numberDrafts[field] = null
      }, () => {
        this.errorMessage = NUMBER_FIELDS[field].failed
      })
    },
    // Same rules as the backend's default_calculation_method
    defaultMethod (row) {
      if (row.task.master_type === 'teaching') {
        return 'scheduled'
      }
      if (row.task.from_date && row.task.to_date) {
        return 'actual_days'
      }
      return 'school_days'
    },
    // Dated tasks (e.g. lejrskole, emneuger) are prorated by the weekdays of the task that fall inside the period.
    actualDaysHours (row) {
      const {from_date: from, to_date: to} = row.task
      if (!from || !to) {
        return 0
      }
      const overlapStart = moment.max(moment(from), moment(this.period.startDate))
      const overlapEnd = moment.min(moment(to), moment(this.period.endDate))
      const taskDays = countWeekdays(from, to)
      if (overlapEnd.isBefore(overlapStart) || taskDays === 0) {
        return 0
      }
      return row.assigned * countWeekdays(overlapStart, overlapEnd) / taskDays
    },
    rowState (row) {
      return this.rows[row.key] || {
        method: this.defaultMethod(row),
        manual: {assessment: 0}
      }
    },
    updateRow (row, changes) {
      this.rows = {...this.rows, [row.key]: {...this.rowState(row), ...changes}}
    },
    setMethod (row, method) {
      if (this.isClosed) return
      this.updateRow(row, {method})
      this.scheduleCalculationSave()
      if (method !== 'assessment') return
      this.$nextTick(() => {
        const ref = this.$refs['period-' + row.key]
        const input = Array.isArray(ref) ? ref[0] : ref
        if (!input) return
        input.focus()
        input.select()
      })
    },
    setManual (row, method, value) {
      if (this.isClosed) return
      this.updateRow(row, {manual: {...this.rowState(row).manual, [method]: value}})
      this.scheduleCalculationSave()
    },
    isManualMethod (method) {
      const found = this.methods.find(item => item.id === method)
      return Boolean(found && found.isManual)
    },
    methodHours (row, method) {
      switch (method) {
        case 'scheduled':
          return row.scheduled
        case 'school_days':
          return row.assigned * this.schoolDaysRatio
        case 'actual_days':
          return this.actualDaysHours(row)
        default: {
          const value = toNullableNumber(this.rowState(row).manual[method])
          return value === null ? 0 : value
        }
      }
    },
    rowHours (row) {
      const method = this.rowState(row).method
      const calculated = this.calculatedValues[row.index]
      const isSaved = this.savedMethods[row.task.id] === method
      if (!this.isManualMethod(method) && isSaved && calculated && calculated.value !== null && calculated.value !== undefined) {
        return toNumber(calculated.value)
      }
      return this.methodHours(row, method)
    },
    groupHours (group) {
      return group.rows.reduce((sum, row) => sum + this.rowHours(row), 0)
    },
    format (value) {
      return toNumber(value).toFixed(2)
    },
    formatDays (value) {
      return String(Math.round(toNumber(value) * 100) / 100).replace('.', ',')
    },
    formatDateRange (fromDate, toDate) {
      if (!fromDate && !toDate) return ''
      const from = fromDate ? moment(fromDate) : null
      const to = toDate ? moment(toDate) : null
      if (from && !to) return from.format('DD-MM-YYYY')
      if (!from && to) return to.format('DD-MM-YYYY')
      if (from.isSame(to, 'day')) return from.format('DD-MM-YYYY')
      return from.format('DD-MM-YYYY') + ' - ' + to.format('DD-MM-YYYY')
    }
  },
  created () {
    this._numberSaveTimers = {}
    if (this.savedStatement) {
      this.applyStatement(this.savedStatement)
      this.loadOriginal()
    }
  },
  beforeDestroy () {
    clearTimeout(this._preparationSaveTimer)
    clearTimeout(this._calculationSaveTimer)
    clearTimeout(this._loadTimer)
    this.clearNumberDrafts()
  }
}
</script>

<style scoped>
.d-wizard__filters {
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  margin: -12px -24px 1.5rem;
  padding: 1.25rem 24px;
  background-color: #ffffff;
  border-bottom: 1px solid #d8dee6;
}

.d-wizard__filters .d-input-group {
  margin-bottom: 0;
}

.d-wizard__filters--locked {
  pointer-events: none;
}

.d-wizard__filters--locked > * {
  opacity: 0.6;
}

.d-wizard__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  background-color: #f6f9fc;
  border: 1px solid #e2e8f0;
}

.d-wizard__status--closed {
  background-color: #f0fef4;
  border-color: #ceeed7;
  color: #008236;
}

.do-table--locked tr td.d-method,
.do-table--locked .d-method__label {
  cursor: default;
}

.d-wizard__filters .d-input-group:first-child {
  margin-left: 0;
}

.d-wizard__filters .do-select {
  height: 38px;
  margin-top: 3px;
}

.d-overview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.d-overview__col {
  background-color: #ffffff;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgb(0 0 0 / 7%);
}

.d-overview__col--active {
  box-shadow: 0 0 0 2px #468AD7, 0 4px 12px rgb(70 138 215 / 20%);
}

.d-overview__col--active .d-overview__title {
  color: #468AD7;
}

.d-overview .d-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e2e8f0;
  font-size: 1rem;
  color: #3d4340;
  margin-bottom: 1rem;
}

.d-overview .d-table:last-child {
  margin-bottom: 0;
}

.d-overview .d-table > thead,
.d-overview .d-table > tfoot {
  border: 1px solid #e2e8f0;
  text-transform: uppercase;
  font-weight: bold;
  text-align: left;
  background: #f6f9fc;
}

.d-overview .d-table > thead > tr:first-child,
.d-overview .d-table > tfoot > tr:first-child {
  color: #8898aa;
}

.d-overview .d-table > thead > tr > th,
.d-overview .d-table > tfoot > tr > td {
  padding: 5px;
}

.d-overview .d-table tr th h4 {
  margin: 0;
  color: #3e4450;
}

.d-overview .d-table__overview tbody tr {
  border: 1px solid #e2e8f0;
}

.d-overview .d-table__overview tbody tr:nth-child(even) {
  background-color: #f8fafb;
}

.d-overview .d-table__overview tbody tr td {
  padding: 0.25rem;
}

.d-overview .is-number {
  text-align: right;
}

.d-bagde.d-badge--warning {
  border-radius: 4px;
  background-color: #fef3f2;
  border: 1px solid #fbdfde;
  color: #b91c1c;
  padding: 2px 4px;
  font-size: 12px;
}

.d-bagde.d-badge--green {
  border-radius: 4px;
  background-color: #f0fef4;
  border: 1px solid #ceeed7;
  color: #008236;
  padding: 2px 4px;
  font-size: 12px;
}

.d-overview__title {
  margin: 0 0 0.75rem;
}

.d-overview .icon-question3 {
  margin-left: 0.25rem;
  cursor: help;
}

.d-overview__days {
  color: #8898aa;
}

.d-overview__rest-status {
  margin-left: 0.5rem;
  font-size: 0.85em;
}

.d-overview__help {
  margin-right: 0.35rem;
  color: #8898aa;
  font-size: 0.85em;
  cursor: help;
}

.d-overview__vacation {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.d-overview__input {
  width: 60px;
  text-align: right;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  background: #fff;
  padding: 1px 6px;
}

.d-overview__input:focus {
  border-color: #468AD7;
  outline: none;
}

.d-overview__input:disabled {
  border-color: transparent;
  background: transparent;
}

.d-overview__title span {
  font-weight: normal;
  color: #8898aa;
  margin-left: 0.5rem;
}

.d-wizard__intro {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.d-wizard__legend {
  flex-direction: column;
  align-items: flex-start;
  margin-left: auto;
  max-width: 44rem;
}

.d-wizard__legend-list {
  margin: 0.25rem 0 0;
  padding-left: 1.25rem;
}

.d-wizard__legend-list li {
  list-style: decimal;
}

.d-wizard__legend-list li + li {
  margin-top: 0.25rem;
}

.d-wizard__loading {
  margin: 2rem 0;
  text-align: center;
}

.d-wizard__error {
  color: var(--red);
}

.do-table {
  border-collapse: collapse;
  border: 1px solid #d8d8d8;
  background-color: #ffffff;
  width: 100%;
  margin-bottom: 0.75rem;
  color: #3d4340;
  box-shadow: 0 1px 2px rgb(0 0 0 / 7%);
}

.do-table tr th,
.do-table tr td {
  border-right: 1px solid #d8d8d8;
  border-bottom: 1px solid #e0e0e0;
}

.do-table tr th:last-child,
.do-table tr td:last-child {
  border-right: none;
}

.do-table tr:last-child td {
  border-bottom: none;
}

.do-table tr th {
  padding: 4px 6px;
  color: #3d4340;
  font-weight: bold;
  white-space: nowrap;
  background: #fff;
  text-align: center;
  border-bottom-color: #d8d8d8;
}

.do-table tr th:first-child,
.do-table tr td:first-child {
  text-align: left;
}

.do-table tr td {
  padding: 1px 8px;
  line-height: 1.3;
  text-align: right;
}

.do-table tbody tr:nth-child(odd) {
  background-color: #f8fafb;
}

.do-table tbody tr:hover {
  background-color: aliceblue;
}

.do-table--summary {
  width: 400px;
}

.do-table tr .d-dates {
  text-align: left;
  white-space: nowrap;
}

.do-table .icon-question3 {
  margin-left: 0.25rem;
  cursor: help;
}

.do-table tr th.d-method-group {
  text-align: center;
  color: #3e4450;
}

.do-table tr th.d-method {
  font-weight: bold;
  border-right: 1px solid #d8d8d8;
}

.do-table tr td.d-method {
  padding: 0;
  cursor: pointer;
}

.do-table tr .d-method {
  text-align: center;
  color: #8898aa;
}

.d-method--selected {
  color: #3e4450;
}

.d-method__label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 26px;
  margin: 0;
  cursor: pointer;
}

.d-method--selected .d-method__label {
  box-shadow: inset 0 0 0 1px #468AD7;
}

.do-table tr td.d-period-hours {
  text-align: right;
}

.d-method__input {
  display: block;
  width: 72px;
  margin-left: auto;
  text-align: right;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  background: #fff;
  padding: 2px 6px;
}

.d-method__input:focus {
  border-color: #468AD7;
  outline: none;
}

.d-diff td {
  padding: 0.5rem 8px !important;
  font-weight: bold;
}

.d-negative {
  color: var(--red);
}
</style>



// WEBPACK FOOTER //
// AssignHoursWorkedHoursStatement.vue?b4e2dc80